import { useState } from "react"
import { PencilSimple } from "@phosphor-icons/react"
import { t } from "i18next"
import { useUser } from "@/apis/bff.ts"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardBody,
  CardBodyHeader,
  CardBodyHeaderTitle,
  CardHeader,
  CardHeaderTitle,
} from "@/components/ui/card.tsx"
import { UserProvider } from "@/components/user-wrapper.tsx"
import { formatDateString } from "@/lib/format-date.ts"
import { CaveDialog } from "@/modules/patients/information/cave/cave-dialog.tsx"
import { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"

type CaveEntryType = {
  message: string
  updatedBy: string
  updatedAt: string
}

interface CaveProps {
  isEditMode?: boolean
  caveEntry?: CaveEntryType
  country: Country
}

const Cave = ({ isEditMode, caveEntry, country }: CaveProps) => {
  const user = useUser()
  const hasCavePermission = user.isOneOf(["MedicalSupervisor"])
  const [openCave, setOpenCave] = useState(false)
  const closeDialog = () => {
    setOpenCave(false)
  }

  return (
    <Card className="animate-fade-in-top w-full">
      {openCave && (
        <CaveDialog open={openCave} closeDialog={closeDialog} message={caveEntry?.message} country={country} />
      )}
      <CardHeader>
        <div className="flex items-center gap-4">
          <CardHeaderTitle data-testid="card-header-title-content">{t("patient.information.cave")}</CardHeaderTitle>
        </div>
        {isEditMode && hasCavePermission && (
          <Button
            data-testid="edit-cave-button"
            variant="circle"
            className="mx-2 flex h-10 w-10 cursor-pointer items-center justify-center p-0"
            onClick={() => setOpenCave(true)}>
            <PencilSimple className="text-on-surface-subdued" />
          </Button>
        )}
      </CardHeader>

      {caveEntry && (
        <CardBody>
          <CardBodyHeader className={!caveEntry.message ? "pb-5" : ""}>
            {caveEntry.message && <CardBodyHeaderTitle>{caveEntry.message}</CardBodyHeaderTitle>}
          </CardBodyHeader>
          <div className="pb-5 pl-5">
            <div className="text-on-surface-subdued mr-auto flex items-center gap-2 text-xs text-ellipsis">
              <UserProvider
                userName={caveEntry.updatedBy}
                render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={user.userName} />}
              />
              <div data-testid="appointment-status-indicator" className="truncate">
                {!caveEntry.message ? t("patient.information.deletedOn") : t("patient.information.updatedOn")}{" "}
                {formatDateString(caveEntry.updatedAt)}
              </div>
            </div>
          </div>
        </CardBody>
      )}
    </Card>
  )
}
export { Cave, type CaveEntryType }
